import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../common/types';
import { looksLikeUrl, toResolvableUrl } from '../../smartLinks/creationSheet/looksLikeUrl';
import { useDebouncedValue } from '../../smartLinks/catalog/useDebouncedValue';
import { CatalogClaimSnapshot } from '../../smartLinks/catalog/types';
import { AddReleaseInput } from '../../smartLinks/catalog/useAddRelease';
import { useSongSearch } from './useSongSearch';
import { useLinkSong } from './useLinkSong';
import { usePickedSongSnapshot } from './usePickedSongSnapshot';
import { SongSearchResults } from './SongSearchResults';
import { SongResultRow } from './SongResultRow';
import { AddSongSaveBar } from './AddSongSaveBar';
import { addSongMessages } from './addSongMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './AddSongView.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** What Save of "It is out" adds: a catalog track (claim) or a pasted link. */
export type ReleasedSongChoice =
  | { kind: 'claim'; snapshot: CatalogClaimSnapshot }
  | { kind: 'link'; input: AddReleaseInput };

interface ReleasedSongPickerProps {
  artistName: string;
  isSaving: boolean;
  onStarted: (kind: 'claim' | 'link') => void;
  onSave: (choice: ReleasedSongChoice) => void;
  onAddUnreleased: () => void;
  /** Shown above Save once a song is picked (the Bio Link tick, design smartlink-editor e1 note). */
  confirmSlot?: React.ReactNode;
  /** The text the field starts with (the title typed on step 1), so the search runs at once. */
  initialQuery?: string;
}

const SEARCH_DELAY_MS = 300;

/**
 * "It is out" (design c1): one field takes a link or a search. A link goes through the resolver of
 * the Catalog "Add a release" sheet; text runs the Catalog claim search, and a pick is claimed as
 * the artist's own song. Save stays off until a song is picked.
 */
const ReleasedSongPickerComponent: React.FC<ReleasedSongPickerProps> = ({
  artistName,
  isSaving,
  onStarted,
  onSave,
  onAddUnreleased,
  confirmSlot,
  initialQuery,
}) => {
  const intl = useIntl();
  const [value, setValue] = useState(initialQuery ?? '');
  const [pickedId, setPickedId] = useState<string>();
  const debounced = useDebouncedValue(value, SEARCH_DELAY_MS);
  const isLink = looksLikeUrl(debounced);
  const url = isLink ? toResolvableUrl(debounced) : undefined;
  const search = useSongSearch(isLink ? '' : debounced, artistName);
  const link = useLinkSong(url, artistName);
  const pickedTrack = useMemo(
    () => [...search.own, ...search.others].find((track) => track.id === pickedId),
    [pickedId, search.others, search.own]
  );
  const snapshot = usePickedSongSnapshot(pickedTrack, artistName);

  useEffect(() => {
    if (debounced.trim() !== '') {
      onStarted(looksLikeUrl(debounced) ? 'link' : 'claim');
    }
  }, [debounced, onStarted]);

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setPickedId(undefined);
  }, []);

  const choice = useMemo<ReleasedSongChoice | undefined>(() => {
    if (isLink) {
      return link.input === undefined ? undefined : { kind: 'link', input: link.input };
    }
    return snapshot === undefined ? undefined : { kind: 'claim', snapshot };
  }, [isLink, link.input, snapshot]);

  const save = useCallback(() => {
    if (choice !== undefined) {
      onSave(choice);
    }
  }, [choice, onSave]);

  const title = choice?.kind === 'link' ? choice.input.title : (choice?.snapshot.title ?? pickedTrack?.name);
  const label = intl.formatMessage(addSongMessages.searchLabel);

  return (
    <>
      <div className={styles.field}>
        <span className={styles.label}>{label}</span>
        <div className={styles.searchInput}>
          <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.searchIcon} />
          {/* 16 px text, a search key and no autocorrect on a phone keyboard (QA mob-9). */}
          <Input
            type="search"
            className={styles.searchField}
            value={value}
            onChange={onChange}
            aria-label={label}
            disabled={isSaving}
            enterKeyHint="search"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            autoFocus
          />
        </div>
      </div>
      {isLink && link.state === 'resolving' && (
        <p className={styles.tiny}>{intl.formatMessage(addSongMessages.resolving)}</p>
      )}
      {isLink && link.state === 'bad' && (
        <div className={styles.alertError} role="alert">
          {intl.formatMessage(addSongMessages.errorBadLink)}
        </div>
      )}
      {isLink && link.input !== undefined && (
        <div className={styles.results}>
          <SongResultRow
            id={link.input.title}
            title={link.input.title}
            subtitle={
              link.sourceLabel === undefined ? link.input.artistName : `${link.input.artistName} · ${link.sourceLabel}`
            }
            imageUrl={link.imageUrl}
            isPicked
            disabled={isSaving}
          />
        </div>
      )}
      {!isLink && debounced.trim() !== '' && (
        <SongSearchResults
          query={debounced}
          artistName={artistName}
          search={search}
          pickedId={pickedId}
          disabled={isSaving}
          onPick={setPickedId}
          onAddUnreleased={onAddUnreleased}
        />
      )}
      {choice !== undefined && confirmSlot}
      <AddSongSaveBar
        title={title}
        canSave={choice !== undefined && !isSaving}
        note={intl.formatMessage(addSongMessages.outNote)}
        onSave={save}
      />
    </>
  );
};

export const ReleasedSongPicker = TypedMemo(ReleasedSongPickerComponent);
